// modals/salesReturns/EditSaleReturn.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import { Save, RotateCcw, Clock, DollarSign, X } from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleReturnActions } from '../../action/saleReturnActions';
import { customerActions } from '../../action/customerActions';

const EditSaleReturn = ({ returnId, onSuccess, onClose, isOpen = false }) => {
    const modalRef = useRef(null);

    // Rounding function
    const roundAmount = (amount) => {
        return Math.round(amount);
    };

    const [formData, setFormData] = useState({
        id: '',
        reference_no: '',
        return_date: dayjs().format('YYYY-MM-DD'),
        sale_id: '',
        original_invoice_no: '',
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        no_of_items: 0,
        total_qty: 0,
        subtotal: 0,
        tax: 0,
        discount: 0,
        shipping: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        paid_amount: 0,
        due_amount: 0,
        payment_status: 'unpaid',
        refund_method: '',
        reason: '',
        notes: '',
        status: 'pending',
        items: [],
        credit_info: null
    });

    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [error, setError] = useState(null);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundAmount, setRefundAmount] = useState(0);
    const [refundMethod, setRefundMethod] = useState('');

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'received', label: 'Received' },
        { value: 'completed', label: 'Completed' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Refund method options
    const refundMethodOptions = [
        { value: 'cash', label: 'Cash Refund' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'credit', label: 'Store Credit' },
        { value: 'card', label: 'Card Refund' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'upi', label: 'UPI' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' }
    ];

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('edit-sale-return');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('edit-sale-return');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    // Load return data
    const loadReturnData = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await saleReturnActions.getReturn(returnId);

            if (result.success) {
                const returnData = result.data.return || result.data;
                const items = result.data.items || [];
                const creditInfo = result.data.credit || null;

                // Calculate totals from items
                let totalSubtotal = 0;
                let totalTax = 0;
                let totalQty = 0;

                const formattedItems = items.map(item => {
                    totalSubtotal += item.unit_price * item.quantity;
                    totalTax += item.tax_amount || 0;
                    totalQty += item.quantity;

                    return {
                        ...item,
                        original_quantity: item.quantity,
                        return_quantity: item.quantity,
                        max_return_qty: item.quantity,
                        return_reason: item.reason || ''
                    };
                });

                const grandTotal = totalSubtotal + totalTax;
                const roundedGrandTotal = roundAmount(grandTotal);
                const roundOff = roundedGrandTotal - grandTotal;

                const formattedData = {
                    id: returnData.id,
                    reference_no: returnData.reference_no,
                    return_date: returnData.return_date || dayjs().format('YYYY-MM-DD'),
                    sale_id: returnData.sale_id,
                    original_invoice_no: returnData.original_invoice_no,
                    customer_id: returnData.customer_id,
                    customer_name: returnData.customer_name,
                    customer_phone: returnData.customer_phone,
                    no_of_items: items.length,
                    total_qty: totalQty,
                    subtotal: parseFloat(returnData.subtotal || totalSubtotal),
                    tax: parseFloat(returnData.tax || totalTax),
                    discount: parseFloat(returnData.discount || 0),
                    shipping: parseFloat(returnData.shipping || 0),
                    grand_total: parseFloat(returnData.grand_total || grandTotal),
                    rounded_grand_total: parseFloat(returnData.rounded_grand_total || roundedGrandTotal),
                    round_off: parseFloat(returnData.round_off || roundOff),
                    enable_rounding: returnData.enable_rounding !== undefined ? returnData.enable_rounding : true,
                    paid_amount: parseFloat(returnData.paid_amount || 0),
                    due_amount: parseFloat(returnData.due_amount || (grandTotal - (returnData.paid_amount || 0))),
                    payment_status: returnData.payment_status || 'unpaid',
                    refund_method: returnData.refund_method || '',
                    reason: returnData.reason || '',
                    notes: returnData.notes || '',
                    status: returnData.status || 'pending',
                    items: formattedItems,
                    credit_info: creditInfo
                };

                setFormData(formattedData);
                setOriginalData(formattedData);

                // Set customer
                if (returnData.customer_id) {
                    setSelectedCustomer({
                        value: returnData.customer_id,
                        label: returnData.customer_name,
                        phone: returnData.customer_phone
                    });
                }

                // Set date
                if (returnData.return_date) {
                    setSelectedDate(dayjs(returnData.return_date));
                }

                // Show modal after data is loaded
                setTimeout(() => showBootstrapModal(), 100);

            } else {
                setError(result.message || 'Failed to load return data');
                Swal.fire('Error', result.message || 'Failed to load return data', 'error');
            }
        } catch (error) {
            console.error('Error loading return:', error);
            setError('Failed to load return data');
            Swal.fire('Error', 'Failed to load return data', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: '', label: 'Select Customer' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    // Initialize
    useEffect(() => {
        if (returnId) {
            loadReturnData();
            loadCustomers();
        }

        return () => {
            cleanupModalBackdrops();
        };
    }, [returnId]);

    // Handle isOpen prop
    useEffect(() => {
        if (isOpen && !loading && formData.id) {
            setTimeout(() => showBootstrapModal(), 100);
        }
    }, [isOpen, loading, formData.id]);

    // Calculate totals with rounding
    const calculateTotals = (items, enableRounding = true) => {
        const returnItems = items.filter(item => item.return_quantity > 0);

        const no_of_items = returnItems.length;
        const total_qty = returnItems.reduce((sum, i) => sum + (i.return_quantity || 0), 0);

        const subtotal = returnItems.reduce((sum, i) =>
            sum + (i.unit_price * i.return_quantity), 0);

        const taxAmount = returnItems.reduce((sum, i) =>
            sum + ((i.tax_amount / i.original_quantity) * i.return_quantity), 0);

        const grandTotal = subtotal + taxAmount;

        // Calculate rounded values
        let roundedGrandTotal = grandTotal;
        let roundOff = 0;

        if (enableRounding) {
            roundedGrandTotal = roundAmount(grandTotal);
            roundOff = roundedGrandTotal - grandTotal;
        }

        return {
            no_of_items,
            total_qty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            tax: parseFloat(taxAmount.toFixed(2)),
            grand_total: parseFloat(grandTotal.toFixed(2)),
            rounded_grand_total: parseFloat(roundedGrandTotal.toFixed(2)),
            round_off: parseFloat(roundOff.toFixed(2))
        };
    };

    // Handle return quantity change
    const handleReturnQtyChange = (index, value) => {
        const qty = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const validQty = Math.min(Math.max(0, qty), item.max_return_qty);

            updatedItems[index] = {
                ...item,
                return_quantity: validQty
            };

            const totals = calculateTotals(updatedItems, prev.enable_rounding);

            // Recalculate due amount
            const dueAmount = totals.grand_total - prev.paid_amount;

            return {
                ...prev,
                items: updatedItems,
                ...totals,
                due_amount: dueAmount > 0 ? dueAmount : 0,
                payment_status: dueAmount <= 0 ? 'paid' : (prev.paid_amount > 0 ? 'partial' : 'unpaid')
            };
        });
    };

    // Handle reason change
    const handleReasonChange = (index, value) => {
        setFormData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = {
                ...updatedItems[index],
                return_reason: value
            };
            return {
                ...prev,
                items: updatedItems
            };
        });
    };

    // Handle status change
    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'pending' }));
    };

    // Handle refund method change
    const handleRefundMethodChange = (option) => {
        setFormData(prev => ({
            ...prev,
            refund_method: option?.value || ''
        }));
    };

    // Handle payment status change
    const handlePaymentStatusChange = (option) => {
        setFormData(prev => ({ ...prev, payment_status: option?.value || 'unpaid' }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, return_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle customer change
    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({
            ...prev,
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));
    };

    // Toggle rounding
    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(prev.items, newEnableRounding);

            const dueAmount = totals.grand_total - prev.paid_amount;

            return {
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals,
                due_amount: dueAmount > 0 ? dueAmount : 0
            };
        });
    };

    // Handle paid amount change
    const handlePaidAmountChange = (value) => {
        const paidAmount = parseFloat(value) || 0;
        const grandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
        const validPaidAmount = Math.min(paidAmount, grandTotal);
        const dueAmount = grandTotal - validPaidAmount;

        let paymentStatus = 'unpaid';
        if (validPaidAmount >= grandTotal) paymentStatus = 'paid';
        else if (validPaidAmount > 0) paymentStatus = 'partial';

        setFormData(prev => ({
            ...prev,
            paid_amount: validPaidAmount,
            due_amount: dueAmount,
            payment_status: paymentStatus
        }));
    };

    // Process refund
    const handleProcessRefund = () => {
        if (formData.due_amount <= 0) {
            Swal.fire('Info', 'No pending refund amount', 'info');
            return;
        }

        setRefundAmount(formData.due_amount);
        setRefundMethod(formData.refund_method || 'cash');
        setShowRefundModal(true);
    };

    const handleRefundSubmit = async () => {
        if (refundAmount <= 0) {
            Swal.fire('Error', 'Please enter a valid refund amount', 'error');
            return;
        }

        if (!refundMethod) {
            Swal.fire('Error', 'Please select a refund method', 'error');
            return;
        }

        try {
            setSaving(true);

            const result = await saleReturnActions.processRefund({
                return_id: formData.id,
                refund_amount: refundAmount,
                refund_method: refundMethod,
                notes: `Refund processed during edit`
            });

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Refund processed successfully',
                    timer: 2000
                });

                // Refresh data
                loadReturnData();
                setShowRefundModal(false);
            } else {
                Swal.fire('Error', result.message || 'Failed to process refund', 'error');
            }
        } catch (error) {
            console.error('Error processing refund:', error);
            Swal.fire('Error', 'Failed to process refund', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Validate form
    const validateForm = () => {
        if (!formData.customer_id) {
            Swal.fire('Error', 'Please select a customer', 'error');
            return false;
        }

        const returnItems = formData.items.filter(item => item.return_quantity > 0);
        if (returnItems.length === 0) {
            Swal.fire('Error', 'At least one item must be returned', 'error');
            return false;
        }

        return true;
    };

    // Check if data changed
    const hasChanges = () => {
        if (!originalData) return false;

        console.log('Checking changes:', {
            original: originalData.status,
            current: formData.status,
            originalPayment: originalData.payment_status,
            currentPayment: formData.payment_status,
            originalRefund: originalData.refund_method,
            currentRefund: formData.refund_method,
            originalReason: originalData.reason,
            currentReason: formData.reason
        });

        // Check basic fields
        if (formData.status !== originalData.status) return true;
        if (formData.payment_status !== originalData.payment_status) return true;
        if (formData.refund_method !== originalData.refund_method) return true;
        if (formData.reason !== originalData.reason) return true;
        if (formData.notes !== originalData.notes) return true;
        if (formData.return_date !== originalData.return_date) return true;
        if (formData.enable_rounding !== originalData.enable_rounding) return true;
        if (formData.paid_amount !== originalData.paid_amount) return true;

        // Check items
        if (formData.items.length !== originalData.items.length) return true;

        for (let i = 0; i < formData.items.length; i++) {
            const newItem = formData.items[i];
            const oldItem = originalData.items[i];

            if (!oldItem) return true;

            if (newItem.return_quantity !== oldItem.return_quantity) return true;
            if (newItem.return_reason !== oldItem.return_reason) return true;
        }

        return false;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        if (!hasChanges()) {
            Swal.fire('Info', 'No changes to save', 'info');
            return;
        }

        try {
            setSaving(true);

            const returnItems = formData.items.filter(item => item.return_quantity > 0);

            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

            const dataToSend = {
                id: formData.id,
                return_date: formData.return_date,
                status: formData.status,
                payment_status: formData.payment_status,
                refund_method: formData.refund_method,
                reason: formData.reason,
                notes: formData.notes,
                paid_amount: formData.paid_amount,
                due_amount: formData.due_amount,
                grand_total: finalGrandTotal,
                rounded_grand_total: formData.rounded_grand_total,
                round_off: formData.round_off,
                enable_rounding: formData.enable_rounding,
                items: returnItems.map(item => ({
                    id: item.id,
                    quantity: item.return_quantity,
                    reason: item.return_reason
                }))
            };

            console.log('Sending update data:', dataToSend);

            // Use update API
            const result = await saleReturnActions.updateReturn(returnId, dataToSend);

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Return updated successfully',
                    timer: 2000
                });

                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to update return', 'error');
            }
        } catch (error) {
            console.error('Error updating return:', error);
            Swal.fire('Error', 'Failed to update return', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (hasChanges()) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // Format date
    // const formatDate = (date) => {
    //     return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    // };

    // Calculate current grand total
    const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

    // Calculate return items
    const returnItems = formData.items.filter(item => item.return_quantity > 0);
    const totalReturnItems = returnItems.length;
    const totalReturnQty = returnItems.reduce((sum, i) => sum + i.return_quantity, 0);

    // Determine if the return is in an editable state
    const isEditable = formData.status !== 'completed' && formData.status !== 'cancelled';

    // If still loading, show loading overlay
    if (loading) {
        return (
            <div className="modal fade" id="edit-sale-return" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Sale Return</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading return data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If error, show error modal
    if (error || !formData.id) {
        return (
            <div className="modal fade" id="edit-sale-return" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title text-danger">Error</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <p>{error || 'Failed to load return data'}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="modal fade" id="edit-sale-return" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Sale Return - {formData.reference_no}</h5>
                            <button type="button" className="btn-close" onClick={handleCancel}></button>
                        </div>

                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                {/* Reference and Date Row */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Return Reference</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.reference_no}
                                            readOnly
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Return Date</label>
                                        <DatePicker
                                            value={selectedDate}
                                            onChange={handleDateChange}
                                            format="DD/MM/YYYY"
                                            className="form-control"
                                            style={{ width: '100%' }}
                                            disabled={!isEditable}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Original Invoice</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.original_invoice_no || 'N/A'}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                {/* Customer Section */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Customer Name</label>
                                        <Select
                                            options={customers}
                                            value={selectedCustomer}
                                            onChange={handleCustomerChange}
                                            placeholder="Select Customer"
                                            isClearable
                                            isDisabled={!isEditable}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Phone</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.customer_phone}
                                            readOnly
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Status</label>
                                        <Select
                                            options={statusOptions}
                                            value={statusOptions.find(s => s.value === formData.status)}
                                            onChange={handleStatusChange}
                                            placeholder="Select status"
                                            isDisabled={!isEditable}
                                        />
                                    </div>
                                </div>

                                {/* Items Table */}
                                {formData.items.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-2">Return Items</h6>
                                        <div className="table-responsive border rounded">
                                            <table className="table table-bordered mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Product Code</th>
                                                        <th>Product Name</th>
                                                        <th className="text-end">Original Qty</th>
                                                        <th className="text-end">Return Qty</th>
                                                        <th className="text-end">Rate</th>
                                                        <th className="text-end">Tax %</th>
                                                        <th className="text-end">Return Amount</th>
                                                        <th>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.items.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm bg-light"
                                                                    value={item.product_code || ''}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <strong>{item.product_name}</strong>
                                                            </td>
                                                            <td className="text-end">
                                                                {item.original_quantity}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm text-end"
                                                                        value={item.return_quantity}
                                                                        onChange={(e) => handleReturnQtyChange(index, e.target.value)}
                                                                        min="0"
                                                                        max={item.max_return_qty}
                                                                        step="0.1"
                                                                        style={{ width: '80px' }}
                                                                        disabled={!isEditable}
                                                                    />
                                                                    <span className="ms-1 text-muted small">
                                                                        / {item.max_return_qty}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">
                                                                {formatCurrency(item.unit_price)}
                                                            </td>
                                                            <td className="text-end">
                                                                {item.tax_percent}%
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {item.return_quantity > 0 ? (
                                                                    formatCurrency(
                                                                        (item.unit_price * item.return_quantity) +
                                                                        ((item.tax_amount / item.original_quantity) * item.return_quantity)
                                                                    )
                                                                ) : '-'}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.return_reason || ''}
                                                                    onChange={(e) => handleReasonChange(index, e.target.value)}
                                                                    placeholder="Reason"
                                                                    disabled={!isEditable}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Summary Section */}
                                {totalReturnItems > 0 && (
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">No of Items</span>
                                                        <span className="fw-bold text-primary">{totalReturnItems}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">Total Qty</span>
                                                        <span className="fw-bold text-info">{totalReturnQty}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">Subtotal</span>
                                                        <span className="fw-bold text-success">{formatCurrency(formData.subtotal)}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">Tax</span>
                                                        <span className="fw-bold text-warning">{formatCurrency(formData.tax)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="bg-primary text-white p-3 rounded">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h5 className="mb-0 text-white">Return Total</h5>
                                                    <div className="d-flex align-items-center">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${formData.enable_rounding ? 'btn-light' : 'btn-outline-light'} me-2`}
                                                            onClick={handleToggleRounding}
                                                            title={formData.enable_rounding ? "Rounding Enabled" : "Rounding Disabled"}
                                                            disabled={!isEditable}
                                                        >
                                                            <RotateCcw size={14} className="me-1" />
                                                            {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                                        </button>
                                                        <h3 className="mb-0 text-white">
                                                            {formatCurrency(currentGrandTotal)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                {formData.enable_rounding && formData.round_off !== 0 && (
                                                    <div className="d-flex justify-content-end">
                                                        <small className="text-white-50">
                                                            (Actual: {formatCurrency(formData.grand_total)} |
                                                            Round Off: {formData.round_off > 0 ? '+' : ''}{formatCurrency(formData.round_off)})
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Refund Section */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Refund Method</label>
                                        <Select
                                            options={refundMethodOptions}
                                            value={refundMethodOptions.find(opt => opt.value === formData.refund_method)}
                                            onChange={handleRefundMethodChange}
                                            placeholder="Select method"
                                            isClearable
                                            isDisabled={!isEditable}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Refunded Amount</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">₹</span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.paid_amount}
                                                onChange={(e) => handlePaidAmountChange(e.target.value)}
                                                min="0"
                                                max={currentGrandTotal}
                                                step="0.01"
                                                disabled={!isEditable}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Pending Refund</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">₹</span>
                                            <input
                                                type="text"
                                                className={`form-control bg-light ${formData.due_amount > 0 ? 'text-danger' : 'text-success'} fw-bold`}
                                                value={formatCurrency(formData.due_amount)}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Payment Status</label>
                                        <Select
                                            options={paymentStatusOptions}
                                            value={paymentStatusOptions.find(opt => opt.value === formData.payment_status)}
                                            onChange={handlePaymentStatusChange}
                                            placeholder="Select status"
                                            isClearable
                                            isDisabled={!isEditable}
                                        />
                                    </div>
                                </div>

                                {/* Process Refund Button */}
                                {formData.due_amount > 0 && isEditable && (
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <button
                                                type="button"
                                                className="btn btn-success w-100"
                                                onClick={handleProcessRefund}
                                            >
                                                <DollarSign size={16} className="me-2" />
                                                Process Refund of {formatCurrency(formData.due_amount)}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Credit Info */}
                                {formData.credit_info && (
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <div className="card bg-info bg-opacity-10">
                                                <div className="card-body py-2">
                                                    <div className="d-flex align-items-center">
                                                        <Clock size={16} className="text-info me-2" />
                                                        <span className="fw-bold me-2">Store Credit:</span>
                                                        <span className="text-success fw-bold">
                                                            {formatCurrency(formData.credit_info.amount)}
                                                        </span>
                                                        <span className="badge bg-info ms-2">
                                                            {formData.credit_info.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Reason and Notes */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Return Reason</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.reason}
                                            onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                            placeholder="Overall return reason"
                                            disabled={!isEditable}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Additional Notes</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.notes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="Enter additional notes"
                                            disabled={!isEditable}
                                        />
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="text-end">
                                    <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                                        <X size={14} className="me-2" />
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || !hasChanges()}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} className="me-2" />
                                                Update Return
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Refund Modal */}
            {showRefundModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Process Refund</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRefundModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Refund Amount</label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={refundAmount}
                                            onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                                            min="0.01"
                                            max={formData.due_amount}
                                            step="0.01"
                                            autoFocus
                                        />
                                    </div>
                                    <small className="text-muted">
                                        Max: {formatCurrency(formData.due_amount)}
                                    </small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Refund Method</label>
                                    <Select
                                        options={refundMethodOptions}
                                        value={refundMethodOptions.find(opt => opt.value === refundMethod)}
                                        onChange={(opt) => setRefundMethod(opt?.value || 'cash')}
                                        placeholder="Select method"
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary flex-grow-1"
                                        onClick={() => setShowRefundModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success flex-grow-1"
                                        onClick={handleRefundSubmit}
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <span className="spinner-border spinner-border-sm" />
                                        ) : (
                                            'Process Refund'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

EditSaleReturn.propTypes = {
    returnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool
};

export default EditSaleReturn;